import React, { type FC, useState } from "react";
import clsx from "clsx";
import type { PackType } from "../../../../../../../../../common-deprecated/types/CommonTypes";
import Text from "../../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Incentive from "../../../../../../../shared-components/css-modules/toyota/molecules/Incentive";
import Popover from "../../../../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import SidePanel from "../../../../../../../shared-components/css-modules/toyota/organisms/SidePanel";
import Collapse from "../../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import Icon from "../../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import a11y from "../../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";

import Price from "../../../../common/price/Price";
import styles from "./Pack.module.scss";
import commonPackStyles from "../Pack.module.scss";
import cardStyles from "../../../../../../../shared-components/css-modules/toyota/molecules/Card.module.scss";
import useAphPack from "../../../../../../hooks/useAphPack";
import {
    trackOptionsBasketPackAdd,
    trackOptionsBasketPackRemove,
    trackOptionsPackLearnMore,
} from "../../../../../../utils/tracking";
import { useCarConfigSelector } from "../../../../../../redux/store";
import { getCarDBAssetSrcSet } from "../../../../../../../../shared-logic/utils/imageUtils";
import { showAccessoriesRate } from "../../../../../../utils/financeInsuranceRate";
import AddToBasket from "../../../../common/AddToBasket";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../../utils/labelUtils";
import SanitizedRenderer from "../../../../../../../../../common-deprecated/components/SanitizedRenderer";

export const Pack: FC<{ pack: PackType; firstShownPack?: boolean }> = ({ pack, firstShownPack }) => {
    const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const ariaPacksInfo = useCarConfigLabel("carConfigA11yButtonPacksInfo");

    const packProps = useAphPack(pack);

    if (!packProps) return null;
    const { actions, packContent } = packProps;
    const { selectPack, deselectPack, displayNotifications } = actions;

    const { selected, totalPackContent, renderedEquipment, availability } = packContent;

    /* styles get toggled based on this const */
    const active = selected;

    const handleClickPack = (): void => {
        if (availability.enabled) {
            if (active) {
                trackOptionsBasketPackRemove();
                deselectPack();
            } else {
                trackOptionsBasketPackAdd();
                selectPack();
                displayNotifications();
            }
        }
    };

    return (
        <>
            <article onClick={handleClickPack} className={styles.pack}>
                {/* OPTIONAL BEST-SELLER LABEL */}
                {pack.subtitle && (
                    <Incentive label={pack.subtitle} variant="best-seller" className={styles.highlighted} />
                )}
                <div className={styles.content}>
                    <div className={commonPackStyles["title-wrapper"]}>
                        <Text tag="h4" variant="apheleia-body">
                            {pack.name}
                        </Text>
                        {!!pack.description && (
                            <Popover placement="bottom">
                                <Popover.Trigger>
                                    <Button
                                        variant="clean"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                        aria-label={ariaPacksInfo}
                                    >
                                        <Icon variant="info" sizing="md" aria-hidden="true" />
                                        <span className={a11y["sr-only"]}>
                                            <CarConfigLabel label="carConfigA11yButtonPacksInfo" />
                                        </span>
                                    </Button>
                                </Popover.Trigger>
                                <Popover.Content
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                >
                                    <SanitizedRenderer html={pack.description} />
                                    <Popover.Close>
                                        <Button
                                            variant="apheleia-text"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                            }}
                                            className={commonPackStyles.link}
                                        >
                                            <CarConfigLabel label="carConfigTooltipClose" />
                                        </Button>
                                    </Popover.Close>
                                </Popover.Content>
                            </Popover>
                        )}
                    </div>
                    <aside>
                        <Text variant="apheleia-price">
                            <Price
                                price={pack.price}
                                forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                                showZeroPrice
                            />
                        </Text>
                        <AddToBasket
                            itemName={pack.name}
                            active={active}
                            className={clsx(cardStyles.indicator, commonPackStyles.button)}
                            aria-pressed={selected}
                            data-testid="pack-add-to-basket-button"
                        />
                    </aside>
                </div>
                <footer className={styles.footer}>
                    <Button
                        variant="apheleia-text"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            trackOptionsPackLearnMore();
                            setIsSidePanelOpen(true);
                        }}
                        autoFocus={firstShownPack}
                    >
                        <CarConfigLabel label="carConfigPacksExtaCount" params={{ count: totalPackContent }} />
                    </Button>
                </footer>
            </article>
            <SidePanel
                open={isSidePanelOpen}
                onRequestClose={() => {
                    setIsSidePanelOpen(false);
                }}
            >
                <header className={commonPackStyles["panel-header"]}>
                    <CarConfigLabel
                        label="carConfigPacksSidepanelTitle"
                        params={{ title: pack.name, count: totalPackContent }}
                        useHTML
                    />
                </header>
                <div className={commonPackStyles["panel-content"]}>
                    {renderedEquipment.map((item) => (
                        <div key={item.id} className={commonPackStyles["panel-item"]}>
                            <Collapse summary={item.name} className={commonPackStyles.details}>
                                <figure className={commonPackStyles["panel-item-image"]}>
                                    <img
                                        {...getCarDBAssetSrcSet(
                                            commonSettings,
                                            [
                                                { width: 380, height: 380 },
                                                { width: 450, height: 450 },
                                                { width: 504, height: 504 },
                                                { width: 800, height: 800 },
                                                { width: 1000, height: 1000 },
                                            ],
                                            item.image,
                                        )}
                                        width="380"
                                        height="380"
                                        sizes="(max-width: 1199px) 100vw, 33vw"
                                        alt=""
                                        role="presentation"
                                    />
                                </figure>
                                <Text variant="apheleia-body" spacing="spacing-0">
                                    {item.description}
                                </Text>
                            </Collapse>
                        </div>
                    ))}
                </div>
            </SidePanel>
        </>
    );
};
